import { v1 as uuid } from 'uuid'
import { useFlag } from '@sketch/modules-common'

import {
  MoveAnnotationMutationVariables,
  useMoveAnnotationMutation,
} from '@sketch/gql-types'

import { useStableHandler } from '@sketch/utils'

import { MouseCoordinates, VariablesWithAdditionalParameters } from '../types'
import { useAnnotationOverlayOrigin } from '../hooks'
import { getDotPositioningFromMouseCoordinates } from '../utils'
import { moveAnnotationInCache } from '../utils/annotationCacheOperations'
import {
  getBaseAnnotation,
  getOptimisticArtboardSubjectFromInputSubject,
  getOptimisticSubjectFromInputSubject,
  isArtboardAnnotationSubjectInput,
} from '../utils/cache'
import { AnnotationQueryVariablesContextValue } from '../context/AnnotationQueryVariablesProvider/context'

const createOptimisticResponse = <T extends MoveAnnotationMutationVariables>(
  ...args: VariablesWithAdditionalParameters<T>
) => {
  const [variables, additionalParams] = args
  const { annotationIdentifier, coordinates, subject } = variables

  const currentSubject = !isArtboardAnnotationSubjectInput(subject)
    ? getOptimisticSubjectFromInputSubject(subject)
    : getOptimisticArtboardSubjectFromInputSubject(
        subject,
        additionalParams?.permanentPageId!
      )

  return {
    __typename: 'RootMutationType',
    moveAnnotation: {
      __typename: 'MoveAnnotationResponse',
      annotation: {
        __typename: 'Annotation',
        identifier: annotationIdentifier,
        subscriptionStatus: 'ON',
        currentSubject,
        coordinates: {
          ...coordinates,
          __typename: 'AnnotationCoordinates',
          identifier: uuid(),
        },
        share: {
          __typename: 'Share',
          identifier: uuid(),
          subscriptionStatus: 'ON',
        },
      },
    },
  } as const
}

const useMoveAnnotation = (params: AnnotationQueryVariablesContextValue) => {
  const [{ origin, scale }] = useAnnotationOverlayOrigin() || []
  const includeHasUnreadComments = useFlag('pages-unread-annotations')

  const [mutation] = useMoveAnnotationMutation({
    onError: 'show-toast',
    update: (cache, { data }) => {
      const { annotation } = data?.moveAnnotation || {}
      if (!annotation) {
        return
      }

      const { shareIdentifier, versionIdentifier } = params

      /**
       * Check if BaseAnnotation fragment exists
       * if it does it means that a listing has been loaded with this
       * identifier, meaning we need to update the listings
       */
      const baseAnnotation = getBaseAnnotation(cache, annotation.identifier)

      baseAnnotation &&
        moveAnnotationInCache(
          cache,
          { shareIdentifier, versionIdentifier: versionIdentifier || '' },
          baseAnnotation
        )
    },
  })

  return useStableHandler(
    (annotationIdentifier: string, mouseCoordinates: MouseCoordinates) => {
      if (!origin?.current || !scale?.current) {
        return
      }

      const {
        position,
        permanentArtboardId,
      } = getDotPositioningFromMouseCoordinates(
        mouseCoordinates,
        origin.current,
        scale.current
      )

      const permanentPageId = params.permanentPageId || ''
      const baseVariables = {
        annotationIdentifier,
        coordinates: position,
        includeHasUnreadComments,
      }

      const subject = permanentArtboardId
        ? {
            type: 'ARTBOARD' as const,
            permanentId: permanentArtboardId,
          }
        : {
            type: 'PAGE' as const,
            permanentId: permanentPageId,
          }

      const variables = { ...baseVariables, subject }
      const optimisticResponse = !isArtboardAnnotationSubjectInput(subject)
        ? () => createOptimisticResponse(variables)
        : () =>
            createOptimisticResponse(
              { ...baseVariables, subject },
              { permanentPageId }
            )

      return mutation({
        variables,
        optimisticResponse,
      })
    }
  )
}

export default useMoveAnnotation
